import {
  ConfirmDialog,
  convert,
  convertBack,
  countryOptions,
  Dialog,
  formatMoney,
  getRate,
  getTypical,
  InvalidInput,
  modes,
  NumberField,
  Pick,
  RangeField,
  type Mode,
  type MoneyRange,
  type Plan,
  type PlanStop,
  type Point,
  type Spot,
  type RangeInput,
  type RangeValue,
  type StopInput,
  type Typical,
  type TypicalBasis,
  live,
} from '@trippath/shared';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { ModeIcon } from '../icons/ModeIcon';
import { problemKey } from '../../lib/format';
import { SpotField } from '../place/SpotField';
import { TownField } from '../place/TownField';
import { useLive } from '../../app/live';

/** Where a new stop starts: a place picked on the map, with its country's typical prices. */
export interface StopStart {
  city: string;
  country: string | null;
  place: Point | null;
  typical: Typical | null;
}

interface Props {
  /** The trip, for the rate of its stop's money. */
  trip: string;
  currency: string;
  /** The stop as it is, when editing; a new stop starts empty, or from `start`. */
  stop?: PlanStop;
  start?: StopStart;
  onClose: () => void;
  /** Keeps the stop, and whether it is only a change on the way. */
  save: (input: StopInput, transfer: boolean) => Promise<Plan>;
  remove?: () => Promise<Plan>;
  /** Its days are bought, by a ticket there and a ticket on: shown, not changed. */
  daysHeld?: boolean;
  onSaved: (plan: Plan) => void;
  /** The stop as the plan has it now, read again after someone else changed the trip. */
  latest?: PlanStop | null;
}

const zero: RangeValue = { min: 0, max: 0 };
const fromMoney = (range: MoneyRange): RangeValue => ({
  min: range.min.minor,
  max: range.max.minor,
});

function complete(range: RangeValue): RangeInput | null {
  return range.min === null || range.max === null ? null : { min: range.min, max: range.max };
}

/** Adds, edits or removes a stop: where, how long, what it should cost, how the trip goes on. */
export function StopDialog({
  trip,
  currency,
  stop,
  start,
  onClose,
  save,
  remove,
  onSaved,
  daysHeld = false,
  latest = null,
}: Props) {
  const { t, i18n } = useTranslation();
  const first = useRef<HTMLInputElement>(null);
  const typical = start?.typical ?? null;
  const [city, setCity] = useState(stop?.city ?? start?.city ?? '');
  const [country, setCountry] = useState<string | null>(
    stop?.country ?? start?.country ?? null,
  );
  // The town's own point, and the exact place within it when there is one.
  const [place, setPlace] = useState<Point | null>(
    stop?.place && !stop.place.guessed && stop.place.name === null
      ? { lat: stop.place.lat, lon: stop.place.lon }
      : (start?.place ?? null),
  );
  const [spot, setSpot] = useState<Spot | null>(
    stop?.place?.name ? { ...stop.place, name: stop.place.name, detail: '' } : null,
  );
  const near = place ?? (stop?.place ? { lat: stop.place.lat, lon: stop.place.lon } : null);
  const [days, setDays] = useState<number | null>(stop?.days ?? 1);
  // The days someone else has given it while it was open, until one is chosen (the `Live`
  // mockup's question): theirs taken, or mine kept and saved over them.
  const { told } = useLive();
  const [known, setKnown] = useState(stop?.days ?? null);
  const theirs = latest && latest.days !== known ? latest.days : null;
  const changer =
    told.findLast((entry) => entry.who !== null)?.who?.name ?? t('activity.someone');
  // Its prices seen, and typed, in its country's money or the trip's — kept in the trip's, at
  // the rate of its first day.
  const townMoney = stop?.currency ?? null;
  const [inLocal, setInLocal] = useState(false);
  const [perTrip, setPerTrip] = useState<number | null>(null);
  useEffect(() => {
    if (!stop || townMoney === null || townMoney === currency) return;
    void getRate(trip, townMoney, stop.start)
      .catch(() => null)
      .then((found) => {
        setPerTrip(found?.perTrip ?? null);
      });
  }, [trip, stop, townMoney, currency]);
  const shownIn = inLocal && townMoney !== null && perTrip !== null ? townMoney : currency;
  const seen = (range: RangeValue): RangeValue =>
    shownIn === currency || perTrip === null
      ? range
      : {
          min: range.min === null ? null : convertBack(range.min, currency, shownIn, perTrip),
          max: range.max === null ? null : convertBack(range.max, currency, shownIn, perTrip),
        };
  const kept =
    (set: (range: RangeValue) => void) =>
    (range: RangeValue): void => {
      set(
        shownIn === currency || perTrip === null
          ? range
          : {
              min: range.min === null ? null : convert(range.min, shownIn, currency, perTrip),
              max: range.max === null ? null : convert(range.max, shownIn, currency, perTrip),
            },
      );
    };
  // Every night booked: what its rooms cost a night, not an estimate to type.
  const stays = stop?.bookings.filter((b) => b.kind === 'stay' && live(b)) ?? [];
  const bookedNights = stays.reduce((sum, b) => sum + b.nights, 0);
  const nightly =
    stop && stop.openNights === 0 && bookedNights > 0
      ? {
          minor: Math.round(stays.reduce((sum, b) => sum + b.price.minor, 0) / bookedNights),
          currency,
        }
      : null;
  const [accommodation, setAccommodation] = useState(
    stop ? fromMoney(stop.accommodation) : (typical?.bed ?? zero),
  );
  const [food, setFood] = useState(stop ? fromMoney(stop.food) : (typical?.food ?? zero));
  const [local, setLocal] = useState(stop ? fromMoney(stop.local) : (typical?.local ?? zero));
  const [activities, setActivities] = useState(
    stop ? fromMoney(stop.activities) : (typical?.fun ?? zero),
  );
  // Where the typical prices filled in come from, when they are an estimate.
  const [basis, setBasis] = useState<TypicalBasis | null>(
    stop ? null : (typical?.basis ?? null),
  );
  const [mode, setMode] = useState<Mode | null>(stop?.out?.mode ?? null);
  const [estimate, setEstimate] = useState(stop?.out ? fromMoney(stop.out.estimate) : zero);
  // How a way on is picked: its price what such a way typically costs, unless one was typed —
  // nothing yet, or the last way's typical price untouched.
  const typicalOf = (way: Mode | null) => stop?.ways.find((one) => one.mode === way)?.estimate;
  const pickMode = (next: Mode) => {
    const was = typicalOf(mode);
    const untouched =
      (estimate.min === 0 && estimate.max === 0) ||
      (estimate.min === was?.min.minor && estimate.max === was.max.minor);
    const typical = typicalOf(next);
    setMode(next);
    if (typical && untouched) setEstimate(fromMoney(typical));
  };
  const [unresolved, setUnresolved] = useState(stop?.unresolved ?? false);
  const [transfer, setTransfer] = useState(stop?.transfer ?? false);
  // A ticket bought for the way on keeps how it goes (the server refuses a change too).
  const booked =
    stop?.out && stop.bookings.some((b) => b.kind === 'ticket' && live(b))
      ? stop.out.mode
      : null;
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [failed, setFailed] = useState(false);
  const [removing, setRemoving] = useState(false);
  // Counts the fills from typical prices: each draws the cost fields anew with them.
  const [filled, setFilled] = useState(0);

  // A new stop in euros starts from what the country typically costs, while nothing is typed.
  const untouched = [accommodation, food, local, activities].every(
    (r) => r.min === 0 && r.max === 0,
  );
  const choose = (next: string) => {
    setCountry(next);
    if (stop || currency !== 'EUR' || !untouched) return;
    getTypical(next).then(
      (typical) => {
        const stay = days ?? 1;
        // What was typed while the prices were on their way stays.
        const unless = (next: RangeValue) => (was: RangeValue) =>
          was.min === 0 && was.max === 0 ? next : was;
        setAccommodation(unless(typical.bed));
        setFood(unless(typical.food));
        setLocal(unless(typical.local));
        setActivities(unless({ min: typical.fun.min * stay, max: typical.fun.max * stay }));
        setFilled((n) => n + 1);
        setBasis(typical.basis);
      },
      () => undefined,
    );
  };

  const done = (job: Promise<Plan>) => {
    job.then(onSaved, (error: unknown) => {
      if (error instanceof InvalidInput) {
        setErrors(
          Object.fromEntries(error.fields.map((f) => [f.field, t(problemKey(f.problem))])),
        );
      } else {
        setFailed(true);
      }
    });
  };

  const submit = (event: { preventDefault: () => void }) => {
    event.preventDefault();
    const ranges = {
      accommodation: complete(accommodation),
      food: complete(food),
      local: complete(local),
      activities: complete(activities),
    };
    const leg = complete(estimate);
    const required = t('problem.required');
    const missing: Record<string, string> = {};
    if (city.trim() === '') missing.city = required;
    if (country === null) missing.country = required;
    if (days === null) missing.days = required;
    for (const [field, range] of Object.entries(ranges)) {
      if (range === null) missing[field] = required;
    }
    if (mode !== null && leg === null) missing['out.estimate'] = required;
    const same = mode === stop?.out?.mode ? stop.out : null;
    setErrors(missing);
    setFailed(false);
    if (
      country === null ||
      days === null ||
      ranges.accommodation === null ||
      ranges.food === null ||
      ranges.local === null ||
      ranges.activities === null ||
      Object.keys(missing).length > 0
    ) {
      return;
    }
    done(
      save(
        {
          city,
          country,
          days,
          accommodation: ranges.accommodation,
          food: ranges.food,
          local: ranges.local,
          activities: ranges.activities,
          // The link the way on was found at, and its times, stay while it goes the same way.
          out:
            mode === null || leg === null
              ? null
              : {
                  mode,
                  estimate: leg,
                  link: same ? same.link : null,
                  departureAt: same ? same.departureAt : null,
                  arrivalAt: same ? same.arrivalAt : null,
                },
          unresolved,
          place: spot ? { lat: spot.lat, lon: spot.lon, name: spot.name } : place,
        },
        transfer,
      ),
    );
  };

  const title = stop ? t('stopForm.editTitle', { city: stop.city }) : t('stopForm.newTitle');

  return (
    <>
      <Dialog open={!removing} onClose={onClose} title={title} initialFocus={first} wide>
        <form className="form" onSubmit={submit} noValidate>
          {theirs !== null && (
            <div className="stop-conflict" role="alert">
              <p>
                {t('live.changedMeanwhile', { name: changer })}{' '}
                <strong>{t('live.theirDays', { name: changer, count: theirs })}</strong>
              </p>
              <div className="dialog-actions">
                <button
                  type="button"
                  className="button"
                  onClick={() => {
                    setDays(theirs);
                    setKnown(theirs);
                  }}
                >
                  {t('live.keepTheirs', { name: changer })}
                </button>
                <button
                  type="button"
                  className="button button-primary"
                  onClick={() => {
                    setKnown(theirs);
                  }}
                >
                  {t('live.keepMine')}
                </button>
              </div>
            </div>
          )}
          <div className="stop-where">
            <TownField
              label={t('stopForm.city')}
              city={city}
              place={spot ?? place}
              guessed={stop?.place?.guessed === true && city === stop.city}
              error={errors.city}
              inputRef={first}
              onType={(typed) => {
                setCity(typed);
                setPlace(null);
                setSpot(null);
              }}
              onPick={(town) => {
                setCity(town.name);
                choose(town.country);
                setPlace({ lat: town.lat, lon: town.lon });
                setSpot(null);
              }}
            />
            <div>
              <Pick
                label={t('stopForm.country')}
                required
                options={countryOptions(i18n.language)}
                value={country}
                onChange={choose}
              />
              {errors.country !== undefined && <p className="field-error">{errors.country}</p>}
            </div>
            {daysHeld ? (
              // Bought: the ticket there and the ticket on hold its days.
              <div className="field">
                <span className="field-label">{t('stopForm.days')}</span>
                <p className="stop-days-held">
                  {t('stopForm.daysHeld', { count: Number(days) })}
                </p>
              </div>
            ) : (
              <NumberField
                // Drawn again with the days taken from someone else.
                key={String(known)}
                label={t('stopForm.days')}
                required
                value={days}
                onChange={setDays}
                min={0}
                max={3650}
                error={errors.days}
              />
            )}
          </div>
          <SpotField
            near={near}
            spot={spot}
            onPick={setSpot}
            onClear={() => {
              setSpot(null);
            }}
          />
          <div className="cost-grid" role="group" aria-label={t('stopForm.costs')}>
            <div className="cost-grid-head">
              <span>{t('stopForm.costs')}</span>
              {/* Seen in the country's money or the trip's: a toggle, the trip's first. */}
              {townMoney !== null && townMoney !== currency && perTrip !== null && (
                <span className="segmented" role="group" aria-label={t('stopForm.shownIn')}>
                  {[currency, townMoney].map((code) => (
                    <button
                      key={code}
                      type="button"
                      aria-pressed={shownIn === code}
                      onClick={() => {
                        setInLocal(code === townMoney);
                      }}
                    >
                      {code}
                    </button>
                  ))}
                </span>
              )}
            </div>
            {nightly ? (
              <div className="range-field cost-booked">
                <span className="field-label">{t('stopForm.accommodation')}</span>
                <p>
                  {t('stopForm.accommodationBooked', {
                    price: formatMoney(
                      perTrip === null || shownIn === currency
                        ? nightly
                        : {
                            minor: convertBack(nightly.minor, currency, shownIn, perTrip),
                            currency: shownIn,
                          },
                      i18n.language,
                    ),
                  })}
                </p>
              </div>
            ) : (
              <RangeField
                key={`accommodation-${String(filled)}-${shownIn}`}
                label={t('stopForm.accommodation')}
                value={seen(accommodation)}
                onChange={kept(setAccommodation)}
                currency={shownIn}
                error={errors.accommodation}
              />
            )}
            <RangeField
              key={`food-${String(filled)}-${shownIn}`}
              label={t('stopForm.food')}
              value={seen(food)}
              onChange={kept(setFood)}
              currency={shownIn}
              error={errors.food}
            />
            <RangeField
              key={`local-${String(filled)}-${shownIn}`}
              label={t('stopForm.local')}
              value={seen(local)}
              onChange={kept(setLocal)}
              currency={shownIn}
              error={errors.local}
            />
            <RangeField
              key={`activities-${String(filled)}-${shownIn}`}
              label={t('stopForm.activities')}
              value={seen(activities)}
              onChange={kept(setActivities)}
              currency={shownIn}
              error={errors.activities}
            />
          </div>
          {(basis === 'eurostat' || basis === 'icp') && (
            <p className="quiet">{t(`stopForm.estimatedBy.${basis}`)}</p>
          )}
          {booked !== null ? (
            // A ticket is bought for the way on: how it goes stays as it is.
            <div className="stop-out">
              <p className="field">
                <span className="field-label">{t('stopForm.out')}</span>
                <span>{t(`mode.${booked}`)}</span>
              </p>
              <p className="quiet">{t('stopForm.booked')}</p>
            </div>
          ) : (
            <div className="stop-out">
              <Pick
                label={t('stopForm.out')}
                options={modes.map((value) => ({
                  value,
                  label: t(`mode.${value}`),
                  icon: <ModeIcon mode={value} size={14} />,
                }))}
                value={mode}
                placeholder={t('stopForm.outNone')}
                searchable={false}
                onChange={pickMode}
                onClear={() => {
                  setMode(null);
                }}
              />
              {mode !== null && (
                <RangeField
                  // Said again when the way changes: its typical price in its fields.
                  key={mode}
                  label={t('stopForm.estimate')}
                  value={estimate}
                  onChange={setEstimate}
                  currency={currency}
                  error={errors['out.estimate']}
                />
              )}
            </div>
          )}
          <label className="check">
            <input
              type="checkbox"
              checked={transfer}
              onChange={(event) => {
                setTransfer(event.target.checked);
              }}
            />
            {t('stopForm.transfer')}
          </label>
          <label className="check">
            <input
              type="checkbox"
              checked={unresolved}
              onChange={(event) => {
                setUnresolved(event.target.checked);
              }}
            />
            {t('stopForm.unresolved')}
          </label>
          {failed && (
            <p className="form-failed" role="alert">
              {t('plan.failed')}
            </p>
          )}
          <div className="dialog-actions">
            {/* Removing, the one that cannot be undone, apart from Cancel and Save at the left. */}
            {remove !== undefined && (
              <button
                type="button"
                className="button button-quiet dialog-action-apart"
                onClick={() => {
                  setRemoving(true);
                }}
              >
                {t('stopForm.remove')}
              </button>
            )}
            <button type="button" className="button" onClick={onClose}>
              {t('dialog.cancel')}
            </button>
            <button type="submit" className="button button-primary">
              {stop ? t('stopForm.save') : t('stopForm.add')}
            </button>
          </div>
        </form>
      </Dialog>
      {remove !== undefined && (
        <ConfirmDialog
          open={removing}
          title={t('stopForm.removeQuestion', { city })}
          body={t('stopForm.removeBody')}
          confirmLabel={t('stopForm.removeConfirm')}
          onCancel={() => {
            setRemoving(false);
          }}
          onConfirm={() => {
            setRemoving(false);
            done(remove());
          }}
        />
      )}
    </>
  );
}
